"use client";

import React, { useCallback, useMemo, useState } from "react";
import type { IChangelogEntry, PageChangelogSchema } from "@/sanity/pages/PageChangelogTemplate";
import ChangelogFilters from "./ChangelogFilters";
import ChangelogList from "./ChangelogList";
import { ALL_CATEGORIES, ChangelogSort, entryFilterKey, groupReleases, listCategories, sortReleases } from "./utils";

export default function ChangelogBody({ data }: { data: PageChangelogSchema }) {
  const entries = useMemo(() => data.entries ?? [], [data.entries]);
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [sort, setSort] = useState<ChangelogSort>("newest");
  // Scroll reveals cover the first paint; the entry keyframe only replays after the user filters or sorts.
  const [hasInteracted, setHasInteracted] = useState(false);

  const changeCategory = useCallback((value: string) => {
    setHasInteracted(true);
    setCategory(value);
  }, []);
  const changeSort = useCallback((value: ChangelogSort) => {
    setHasInteracted(true);
    setSort(value);
  }, []);

  const categories = useMemo(() => listCategories(entries), [entries]);
  const releases = useMemo(() => sortReleases(groupReleases(entries), sort), [entries, sort]);

  const isVisible = useCallback(
    (entry: IChangelogEntry) => category === ALL_CATEGORIES || entryFilterKey(entry) === category,
    [category],
  );
  const visibleCount = releases.filter((release) => release.entries.some(isVisible)).length;

  return (
    <>
      <div className="mt-0 lg:mt-[6.875rem]">
        <ChangelogFilters
          categories={categories}
          category={category}
          onCategoryChange={changeCategory}
          sort={sort}
          onSortChange={changeSort}
          allLabel={data.filterAllLabel || "All"}
          sortLabels={{
            newest: data.sortNewestLabel || "Newest",
            oldest: data.sortOldestLabel || "Oldest",
          }}
        />
      </div>

      <p
        className="sr-only"
        aria-live="polite">
        {visibleCount === 1 ? "1 release shown" : `${visibleCount} releases shown`}
      </p>

      <ChangelogList
        releases={releases}
        isVisible={isVisible}
        animateEntries={hasInteracted}
      />
    </>
  );
}
